import { Auth0Client } from '@auth0/auth0-spa-js';
import type { AuthAccount, AuthClient } from './authClient';

/** Added to the ID token by the `klayo-claims` Post-Login Action; lower-cased, as the API sees it. */
const EMAIL_CLAIM = 'https://klayo.com/email';

/** Error codes from auth0-spa-js that mean the session is gone and only a new login will do. */
const INTERACTION_REQUIRED_ERRORS = new Set([
  'missing_refresh_token',
  'invalid_grant',
  'login_required',
  'consent_required',
  'interaction_required',
]);

export interface Auth0Env {
  AUTH0_DOMAIN?: string;
  AUTH0_CLIENT_ID?: string;
  AUTH0_AUDIENCE?: string;
}

interface AppState {
  returnTo?: string;
}

/** A path on this origin, or the root. `//host` is protocol-relative and would throw in replaceState. */
function safeReturnPath(returnTo: string | undefined): string {
  return returnTo?.startsWith('/') && !returnTo.startsWith('//') ? returnTo : '/';
}

/**
 * Auth0 through auth0-spa-js. Refresh tokens in local storage (no hidden iframe, so no third-party
 * cookie dependency); one access token for the Klayo API audience.
 */
export class Auth0AuthClient implements AuthClient {
  private readonly client: Auth0Client;
  private account: AuthAccount | null = null;

  constructor(env: Auth0Env, client?: Auth0Client) {
    this.client =
      client ??
      new Auth0Client({
        domain: env.AUTH0_DOMAIN ?? '',
        clientId: env.AUTH0_CLIENT_ID ?? '',
        cacheLocation: 'localstorage',
        useRefreshTokens: true,
        useRefreshTokensFallback: false,
        authorizationParams: {
          audience: env.AUTH0_AUDIENCE,
          redirect_uri: window.location.origin,
        },
      });
  }

  async initialize(): Promise<boolean> {
    const params = new URLSearchParams(window.location.search);
    const isCallback = params.has('state') && (params.has('code') || params.has('error'));

    if (isCallback) {
      try {
        const { appState } = await this.client.handleRedirectCallback<AppState>();
        window.history.replaceState(null, '', safeReturnPath(appState?.returnTo));
      } catch (error) {
        // Strip code/state either way, so a reload doesn't replay a spent (or failed) callback.
        window.history.replaceState(null, '', '/');
        throw error;
      }
    } else {
      await this.client.checkSession();
    }

    await this.loadAccount();
    return isCallback;
  }

  getAccount(): AuthAccount | null {
    return this.account;
  }

  async getAccessToken(options?: { forceRefresh?: boolean }): Promise<string> {
    const token = await this.client.getTokenSilently({
      cacheMode: options?.forceRefresh ? 'off' : 'on',
    });
    if (!token) {
      // Only 'cache-only' mode may legitimately return nothing; treat it as a lost session.
      throw Object.assign(new Error('No access token'), { error: 'login_required' });
    }
    return token;
  }

  loginRedirect(): Promise<void> {
    const { pathname, search } = window.location;
    return this.client.loginWithRedirect<AppState>({ appState: { returnTo: pathname + search } });
  }

  logout(): Promise<void> {
    return this.client.logout({ logoutParams: { returnTo: window.location.origin } });
  }

  isInteractionRequired(error: unknown): boolean {
    const code = (error as { error?: unknown } | null)?.error;
    return typeof code === 'string' && INTERACTION_REQUIRED_ERRORS.has(code);
  }

  private async loadAccount(): Promise<void> {
    const user = await this.client.getUser();
    const email = (user?.[EMAIL_CLAIM] as string | undefined) ?? user?.email?.toLowerCase();
    this.account = email ? { email } : null;
  }
}
